<template>
<div>
<collapsible-panel
	panel-size="sm" 
	:always-open="true">
	
	<template slot="panel">
		<h5>Change Order Sections</h5>

		<panel-menu
			:menu-items="changeOrderSections"
			:active-item="activeSection"
			v-on:itemClick="activateSection">
		</panel-menu>

		<p v-if="showDeleteChangeOrder" class="mt-4"><a href="#" v-on:click.prevent="deleteChangeOrder" class="text-danger">Delete Change Order...</a></p>
	</template>

	<template slot="main">
		<i class="fa fa-times icon-button close-edit" v-on:click="backToList"></i>
		
		<p><a :href="'/project/'+$store.project.slug+'/change-orders'" v-on:click.prevent="backToList">
			<i class="fa fa-arrow-left"></i>
			Back to Change Orders
		</a></p>

		<project-change-order-worksheets v-if="activeSection == 'Worksheets'"
			:change-order="changeOrder"
			v-on:changeOrderChanged="fetchChangeOrder"
			v-on:activateSection="activateSection">
		</project-change-order-worksheets>

		<div v-else class="row">
			<div :class="{'col-lg-6' : canSeePdf, 'col-lg-12' : !canSeePdf}">
				<vue-form v-if="activeSection != 'Delivery' || changeOrder.is_finalized == 0"
					v-on:submit="saveChangeOrder(false)" 
					size="sm" 
					:no-top-button="true" 
					:save-button-fixed="true"
					:working="working">

					<div class="d-flex justify-content-between">
						<h3 v-if="'quote_num' in changeOrder && changeOrder.quote_num" class="mb-0">Change Order  #CO{{$store.project.job_num}}-{{changeOrder.quote_num}}</h3>

						<context-menu v-if="changeOrderSlug && changeOrderSlug != 'new'"
							:menu-items="contextMenuItems"
							v-on:itemClick="contextMenuClick">
						</context-menu>
					</div>

					<section v-if="activeSection == 'Details'">
						<field v-if="changeOrderSlug == 'new'"
	 						label="Change Order Type"
							col-class="col-md-4"
							v-model="changeOrder.is_cost_plus"
							:readonly="readonly"
							:params="{
								type:'boolean',
								doubleLabels: ['Quote','Cost Plus']
							}">
						</field>
						<field v-else 
							label="Change Order Type" 
							col-class="col-md-4"
							:params="{type:'editable-static'}"
							:value="changeOrder.is_cost_plus == 1 ? 'Cost Plus' : 'Quote'"
							v-on:click="changeCOType">
						</field>

						<template v-if="changeOrder.is_cost_plus == 1"> 
							<field 
								label="Status"
								col-class="col-sm-5"
								:params="{type: 'select',options: changeOrderStatusOptions()}"
								v-model="changeOrder.quote_status"
								v-on:change="quoteStatusChanged">
							</field>

							<field v-if="changeOrderStatusCompleted"
								label="Date Completed" 
								col-class="col-sm-5" 
								:params="{type:'date'}" 
								v-model="changeOrder.completed">
							</field>

							<alert v-if="$store.project && $store.project.detailed_timelogs != 1"
								:visible="true"
								:unclosable="true"
								classes="alert-warning"
								icon="fa-warning"
								message="Detailed Time Logs is turned off on this project. Field workers will not be able to submit detailed time log claims toward this change order when they clock out.">
							</alert>

						</template>

						<field
							label="Hide from Detailed Reporting"
							col-class="col-sm-5"
							:params="{type: 'boolean'}"
							v-model="changeOrder.hide_from_detailed_reporting">
							<div>
								Toggling this on will hide this change order from being selected on the clock out page's detailed reporting.
							</div>
						</field>

						<field
	 						label="Request ID"
							col-class="col-md-4"
							:readonly="readonly"
							v-model="changeOrder.request_num">
						</field>

						<field
	 						label="Work Requested"
							col-class="stacked"
							:params="{type:'richtext'}"
							:readonly="readonly"
							v-model="changeOrder.description">
						</field>

					</section>

					<section v-if="activeSection == 'Work Completed'">

						<button class="btn btn-light float-right" title="Compile work completed from worksheets" v-on:click="compileWorkCompletedSectionFromWorksheets">
							<i class="fa fa-sync"></i>
						</button>
						<div style="clear: both;"></div>

						<field
	 						label="Work Completed"
							col-class="stacked"
							:params="{type:'richtext'}"
							:readonly="changeOrder.is_finalized == 1"
							v-model="changeOrder.work_completed_description">
						</field>
					</section>

					<invoice-tasks-section v-if="activeSection == 'Tasks'"
						:invoice-tasks="changeOrderTasks"
						:invoice-id="'id' in changeOrder ? changeOrder.id : null"
						billing-document-id-key="quote_id"
						type-name="Change Order"
						:hide-regen-button="!isCostPlus"
						:is-global-service-tasks="true"
						:billing-document-object-resource="billingDocumentObjectResource"
						:is-finalized="changeOrder && 'is_finalized' in changeOrder && changeOrder.is_finalized == 1"
						v-on:confirmRegen="regenerateTasksSection"
						v-on:modelChanged="fetchChangeOrder">
					</invoice-tasks-section>

					<quote-labour-section v-else-if="activeSection == 'Labour' && !isCostPlus"
						:quote-id="'id' in changeOrder ? changeOrder.id : null"
						:labour-quoted="changeOrderLabour"
						:material-labour-total="materialLabourTotal"
						:quote-labour-total="changeOrder.quote_labour_total"
						:quote-object-resource="billingDocumentObjectResource"
						quote-resource="JobChangeOrder"
						:is-finalized="changeOrder && 'is_finalized' in changeOrder && changeOrder.is_finalized == 1"
						v-on:modelChanged="fetchChangeOrder">
					</quote-labour-section>

					<invoice-labour-section v-else-if="activeSection == 'Labour' && isCostPlus"
						:invoice-id="'id' in changeOrder ? changeOrder.id : null"
						:invoice-labour="changeOrderLabour"
						:material-labour-total="materialLabourTotal"
						:billing-document-object-resource="billingDocumentObjectResource"
						:client-rate-overrides="clientRateOverrides"
						:is-finalized="changeOrder && 'is_finalized' in changeOrder && changeOrder.is_finalized == 1"
						v-on:modelChanged="fetchChangeOrder"
						v-on:confirmRegen="regenerateLabourSection">
					</invoice-labour-section>

					<material-section v-else-if="activeSection == 'Materials'"
						:billing-document-materials="changeOrderMaterials"
						:billing-document-assemblies="changeOrderAssemblies"
						:is-quote-section="!changeOrder.is_cost_plus"
						:is-change-order-section="true"
						:hide-regen-button="!isCostPlus"
						:client-invoice-settings="clientQuoteSettings"
						:billing-document-id="'id' in changeOrder ? changeOrder.id : null"
						parent-key="quote_id"
						:billing-document-object-resource="billingDocumentObjectResource"
						:is-finalized="changeOrder && 'is_finalized' in changeOrder && changeOrder.is_finalized == 1"
						v-on:materialLabourChange="materialLabourChange"
						v-on:confirmRegen="regenerateMaterialSection"
						v-on:modelChanged="fetchChangeOrder">
					</material-section>

					<invoice-equipment-section v-else-if="activeSection == 'Equipment'"
						:invoice-equipment="changeOrderEquipment"
						:invoice-id="'id' in changeOrder ? changeOrder.id : null"
						:invoice-object-resource="billingDocumentObjectResource"
						type-name="Change Order"
						:is-finalized="changeOrder && 'is_finalized' in changeOrder && changeOrder.is_finalized == 1"
						:hide-regen-button="!isCostPlus"
						v-on:confirmRegen="regenerateEquipmentSection"
						v-on:modelChanged="fetchChangeOrder">
					</invoice-equipment-section>

					<invoice-additional-charges-section v-else-if="activeSection == 'Additional Charges'"
						:invoice-additional-charges="changeOrderAdditionalCharges"
						:invoice-id="'id' in changeOrder ? changeOrder.id : null"
						invoice-object-resource="JobChangeOrderObject"
						:is-finalized="changeOrder && 'is_finalized' in changeOrder && changeOrder.is_finalized == 1"
						:hide-permitting="true"
						v-on:modelChanged="fetchChangeOrder">
					</invoice-additional-charges-section>

					<invoice-display-settings v-if="activeSection == 'Display Settings'"
						:settings="clientQuoteSettings"
						:customer-settings="customerInvoiceSettings"
						:global-settings="globalInvoiceSettings"
						layer="document"
						:is-for-service="false"
						v-on:settingsChange="invoiceDisplaySettingsChange">
					</invoice-display-settings>

					<invoice-finalizing-section v-else-if="activeSection == 'Finalizing'"
						:invoice-details="changeOrder"
						:is-project-change-quote="true"
						type-name="Change Order"
						:hide-partial-payments="true"
						v-on:detailsChange="setChangeOrder"
						v-on:salesTaxToggle="setChangeOrder">
					</invoice-finalizing-section>

					<button v-if="activeSection != 'Order Items'" type="button" class="btn btn-light float-left" v-on:click="prevStep">
						<i class="fa fa-chevron-left"></i> Previous Step
					</button>

					<button v-if="activeSection != 'Finalizing'" :disabled="working" type="button" class="btn btn-light float-right" v-on:click="nextStep">
						Next Step <i class="fa fa-chevron-right"></i>
					</button>
					<div v-else class="float-right">
						<button class="btn btn-lg btn-primary float-right" v-on:click="saveChangeOrder(true)">
							<i class="fa fa-lock"></i>
							Finalize Change Order
						</button>

						<p>The change order must be finalized to move onto the delivery step</p>
					</div>

				</vue-form>
				<div v-else>

					<invoice-delivery-section class="mt-0"
						:invoice-details="changeOrder"
						:client-invoice-settings="clientQuoteSettings"
						:has-attached-files="false"
						:customer="customer"
						type-name="Change Order"
						api-resource="JobChangeOrder"
						v-on:deFinalize="definalize">

						<template slot="top">
							<context-menu v-if="changeOrderSlug && changeOrderSlug != 'new'"
								:menu-items="contextMenuItems"
								v-on:itemClick="contextMenuClick">
							</context-menu>
						</template>

						<template slot="main">
							<field v-if="changeOrder.quote_status == 'Accepted'" 
								label="Change order ref #"
								col-class="col-sm-5"
								v-model="changeOrderRef"
								v-on:change="saveChangeOrderRef">
							</field>
						</template>

					</invoice-delivery-section>

					<activity-log 
						:submitted-log-json="changeOrder.submitted_log_json"
						:seen-log-json="changeOrder.seen_log_json"
						type-name="Change Order"
						:editing-log-json="changeOrder.editing_log_json">
					</activity-log>
				</div>
				
			</div>
			<div v-if="canSeePdf" class="col-lg-6">
				<h3>Change Order Preview</h3>

				<pdf-js v-if="changeOrder.slug" :url="changeOrderPreviewSrc" :page="1"></pdf-js>
				<p v-else>Save the Change Order to generate a preview</p>
			</div>
		</div>
	</template>
</collapsible-panel>

<modal-dialog :modal-visible="deleteConfirmModal.visible"
	title="Delete Change Order"
	confirm-button-text="Yes, Delete"
	button-class="btn-outline-danger"
	v-on:closeModal="deleteConfirmModal.visible = false"
	v-on:confirm="confirmDeleteChangeOrder">

	Are you sure you want to delete this change order?

</modal-dialog>

<modal-dialog :modal-visible="changeTypeModal.visible"
	title="Change Type"
	confirm-button-text="Yes, Switch Type"
	v-on:closeModal="changeTypeModal.visible = false"
	v-on:confirm="confirmSwitchType">

	Are you sure you want to change this Change Order Type to {{ changeOrder.is_cost_plus == 1 ? 'Quote' : 'Cost Plus' }}?

</modal-dialog>

</div>
</template>
<script type="text/javascript">

import CollapsiblePanel from "@/layout_fragments/CollapsiblePanel"
import PanelMenu from "@/components/PanelMenu"

import VueForm from "@/components/VueForm"
import Field from "@/components/Field"
import Alert from "@/components/Alert"
import FormGroup from "@/components/FormGroup"
import QuoteLabourSection from "@/pages/project/services/invoicing/QuoteLabourSection"
import MaterialSection from "@/pages/project/services/invoicing/MaterialSection"
import InvoiceEquipmentSection from "@/pages/project/services/invoicing/InvoiceEquipmentSection"
import InvoiceFinalizingSection from "@/pages/project/services/invoicing/InvoiceFinalizingSection"
import InvoiceTasksSection from "@/pages/project/services/invoicing/InvoiceTasksSection"
import InvoiceLabourSection from "@/pages/project/services/invoicing/InvoiceLabourSection"
import InvoiceAdditionalChargesSection from "@/pages/project/services/invoicing/InvoiceAdditionalChargesSection"
import InvoiceDisplaySettings from "@/components/InvoiceDisplaySettings"

import InvoiceDeliverySection from "@/components/super_components/InvoiceDeliverySection"
import ProjectChangeOrderWorksheets from "@/pages/project/project_tabs/change_orders/ProjectChangeOrderWorksheets"
import ActivityLog from "@/components/ActivityLog"
import ContextMenu from "@/layout_fragments/ContextMenu"

import ModalDialog from "@/components/ModalDialog"

import InvoiceMaterialMixins from "@/mixins/InvoiceMaterialMixins.js"
import InvoiceMixins from "@/mixins/InvoiceMixins.js"

import PdfJs from "@/components/PdfJs"

import JobMixins from "@/mixins/JobMixins.js"

export default {
	name: "ProjectChangeOrder",
	components: {
		PanelMenu,
		CollapsiblePanel,
		QuoteLabourSection,
		InvoiceLabourSection,
		MaterialSection,
		InvoiceTasksSection,
		InvoiceEquipmentSection,
		InvoiceFinalizingSection,
		InvoiceAdditionalChargesSection,
		InvoiceDeliverySection,
		InvoiceDisplaySettings,
		ProjectChangeOrderWorksheets,
		ActivityLog,
		ContextMenu,
		PdfJs,
		VueForm,
		Field,
		Alert,
		FormGroup,
		ModalDialog
	},
	mixins: [InvoiceMaterialMixins,InvoiceMixins,JobMixins],
	props: ["changeOrderSlug"],
	data() {
		return {
			hasReadFinancials: false,
			hasWritePerms: false,
			changeOrder: {
				is_finalized: 0,
			},
			changeOrderRef: "",
			changeOrderAssemblies: [],
			changeOrderMaterials: [],
			changeOrderLabour: [],
			changeOrderEquipment: [],
			changeOrderAdditionalCharges: [],
			changeOrderTasks: [],
			customer: {},
			materialLabourTotal: 0,
			working: false,
			activeSection: "",
			previewRefresher: 0,
			chargeOutGroups: [],
			clientQuoteSettings: {},
			worksheets: [],
			deleteConfirmModal: {
				visible: false
			},
			changeTypeModal: {
				visible: false,
			},
			clientRateOverrides: [],
			billingDocumentObjectResource: "JobChangeOrderObject"
		}
	},
	watch: {
		changeOrderSlug() {
			if (this.changeOrderSlug)
				this.fetchChangeOrder()
		},
		'$store.project'() {
			if ("customer_id" in this.$store.project) {
				this.fetchCustomer()
				this.setPermFlags()
			}
		}
	},
	computed: {
		changeOrderStatusCompleted() {
			let completedStatuses = ["Complete","Complete, Approved","Complete, Pending Approval"]

			return completedStatuses.indexOf(this.changeOrder.quote_status) >= 0
		},
		customerInvoiceSettings() {
			let settings = {}

			try {
				settings = JSON.parse(this.customer.settings_json)
			}
			catch (e) {}

			return settings
		},
		hasCompleteStatus() {
			if (this.changeOrder && "quote_status" in this.changeOrder)
				return this.changeOrder.quote_status.indexOf('Complete') >= 0

			return false
		},
		readonly() {
			if (this.hasCompleteStatus || ("quote_status" in this.changeOrder && this.changeOrder.quote_status == 'Canceled'))
				return true

			if (this.changeOrder.is_finalized == 1)
				return true

			return false
		},
		isCostPlus() {
			if (this.changeOrder && "is_cost_plus" in this.changeOrder && this.changeOrder.is_cost_plus == 1)
				return true

			return false
		},
		contextMenuItems() {
			let items = []

			let disabled = true
			if (this.changeOrder.is_finalized == 1) 
				disabled = false

			items.push({
				icon: 'fa-lock-open',
				text: "De-finalize for Editing",
				clickFunction: "definalize",
				disabled
			})

			/*
			items.push({
				icon: 'fa-sync',
				text: 'Re-Generate All Sections from Worksheets',
				clickFunction: 'generateFromWorksheets',
				disabled: !disabled
			})
			*/

			/*
			if (this.activeSection == 'Materials') {
				items.push({
					icon: 'fa-sync',
					text: 'Import Materials from Worksheets',
					clickFunction: 'importMaterialsFromWorksheets',
					disabled: !disabled
				})
			}
			else if (this.activeSection == 'Labour') {
				items.push({
					icon: 'fa-sync',
					text: 'Import Labour from Time Logs',
					clickFunction: 'importLabourFromTimeLogs',
					disabled: !disabled
				})
			}
			else if (this.activeSection == 'Equipment') {
				items.push({
					icon: 'fa-sync',
					text: 'Import Equipment from Worksheets',
					clickFunction: 'importEquipmentFromWorksheets',
					disabled: !disabled
				})
			}
			*/
			
			if (this.showDeleteChangeOrder) {
				items.push({
					icon: 'fa-trash',
					text: 'Delete Change Order',
					clickFunction: "deleteChangeOrder",
					itemClass: "text-danger"
				})
			}

			return items
		},
		showDeleteChangeOrder() {
			if (this.getUserRoleType() == 'Basic' && this.$store.userDetails.JobPersonnelRole.find(role => role.job_id == this.$store.project.id))
				return false

			return true
		},
		changeOrderSections() {
			if (this.changeOrderSlug && this.changeOrderSlug != 'new') {
				if (this.changeOrder.is_cost_plus == 1) {

					if (this.hasCompleteStatus) {
						let tabs = [
							"Worksheets",
							"Work Completed",
							"Tasks",
							"Materials",
							"Labour",
							"Equipment",
							"Additional Charges",
							//"Attachments",
							"Display Settings",
						]

						if (this.hasWritePerms)
							tabs.unshift("Details")

						if (this.changeOrder.is_finalized == 1)
							tabs.push("Delivery")
						else
							tabs.push("Finalizing")

						return tabs
					}
					else {
						let tabs = ["Worksheets"]

						if (this.hasWritePerms)
							tabs.unshift("Details")

						return tabs
					}
				}
				else {
					let tabs = [
						"Tasks",
						"Materials",
						"Labour",
						"Equipment",
						"Additional Charges",
						//"Attachments",
						"Display Settings"
					]

					if (this.hasWritePerms)
						tabs.unshift("Details")

					if (this.changeOrder.is_finalized == 1)
						tabs.push("Delivery")
					else
						tabs.push("Finalizing")

					return tabs
				}
			}
			else
				return ["Details"]
		},
		changeOrderPreviewSrc() {
			return window.apiBase+"JobChangeOrder/pdf?slug="+this.changeOrder.slug+"&token="+this.$store.sessionToken+"&humanReadable=true"+"&v="+this.previewRefresher
		},
		canSeePdf() {
			//if user does not have read_financials or write_perms on the project, they can't see the pdf

			if (this.getUserRoleType() == 'Admin' || this.hasModulePermRead("Jobs"))
				return true
			if (this.hasReadFinancials)
				return true
			
			return false
		},
		customerInvoiceSettings() {

		},
	},
	methods: {
		initActiveSection() {
			if (this.hasWritePerms)
				this.activeSection = "Details"
			else {
				if (this.changeOrder.is_cost_plus == 1)
					this.activeSection = "Worksheets"
				else
					this.activeSection = 'Tasks'
			}
		},
		activateSection(section) {
			this.activeSection = section

			if (this.activeSection == 'Labour' && this.chargeOutGroups.length == 0) {
				this.fetchChargeOutGroups()
			}
		},
		backToList() {
			this.$router.push("/project/"+this.$store.project.slug+"/change-orders")
		},
		prevStep() {
			//find index of current and minus one
			this.activeSection = this.changeOrderSections[this.changeOrderSections.indexOf(this.activeSection)-1]
		},
		nextStep() {
			let vm = this
			if (!("id" in this.changeOrder) && this.activeSection == 'Details') {
				this.saveChangeOrder().then(response => {
					vm.$nextTick(() => {
						vm.activeSection = vm.changeOrderSections[vm.changeOrderSections.indexOf(vm.activeSection)+1]
					})
				})
			}
			else
				//find index of current and minus one
				this.activeSection = this.changeOrderSections[this.changeOrderSections.indexOf(this.activeSection)+1]
		},
		contextMenuClick(clickFunction,item) {
			if ("arg" in item)
				this[clickFunction](item.arg)
			else
				this[clickFunction]()
		},
		setPermFlags() {
			this.hasJobPerm("write_perms",this.$store.userDetails.id,this.$store.project.id).then(response => {
				this.hasWritePerms = response
			})

			this.hasJobPerm("read_financials",this.$store.userDetails.id,this.$store.project.id).then(response => {
				this.hasReadFinancials = response
				this.initActiveSection()
			})
		},
		mapChangeOrderObjects(list) {
			return list.map(obj => {
				let fields = JSON.parse(obj.field_values_json)

				fields.id = obj.id
				fields.quote_id = obj.quote_id

				return fields
			})
		},
		invoiceDisplaySettingsChange(settings) {
			let vm = this

			vm.$nextTick(() => {
				let payload = {
					slug: vm.changeOrderSlug,
					job_id: vm.$store.project.id,
					settings_override_json: JSON.stringify(settings)
				}

				vm.clientQuoteSettings = settings

				this.postData("JobChangeOrder/save",payload).then(response => {
					if (response.success)
						vm.previewRefresher++
				})
			})
		},
		setChangeOrder(quoteDetails) {
			this.changeOrder = quoteDetails
			this.saveChangeOrder()
		},
		setQuoteDisplaySettings(json_string) {
			try {
				this.clientQuoteSettings = JSON.parse(json_string)
			}
			catch (e) {
				this.clientQuoteSettings = {}
			}
		},
		setChangeOrderObjects(objs) {
			let vm = this

			vm.changeOrderMaterials = vm.mapChangeOrderObjects(objs.filter(obj => obj.type == "Material"))
			vm.changeOrderAssemblies = vm.mapChangeOrderObjects(objs.filter(obj => obj.type == 'Assembly'))

			vm.changeOrderLabour = vm.mapChangeOrderObjects(objs.filter(obj => obj.type == "Labour"))
			vm.changeOrderEquipment = vm.mapChangeOrderObjects(objs.filter(obj => obj.type == "Equipment"))
			vm.changeOrderTasks = vm.mapChangeOrderObjects(objs.filter(obj => obj.type == "Task"))
			vm.changeOrderAdditionalCharges = vm.mapChangeOrderObjects(objs.filter(obj => obj.type == "Additional"))
		},
		changeCOType() {
			this.changeTypeModal.visible = true

		},
		confirmSwitchType() {
			if (this.changeOrder.is_cost_plus == 0)
				this.changeOrder.is_cost_plus = 1
			else
				this.changeOrder.is_cost_plus = 0

			this.changeTypeModal.visible = false
			this.saveChangeOrder()
		},
		materialLabourChange(total) {
			this.materialLabourTotal = total
		},
		definalize() {
			let vm = this

			let payload = {
				id: this.changeOrder.id,
				is_finalized: 0
			}

			this.postData("JobChangeOrder/save",payload,true).then(response => {
				if (response.success) {
					vm.changeOrder.is_finalized = 0
					vm.activateSection("Finalizing")
					
					vm.fetchChangeOrder()
					vm.alertSuccess("Change order de-finalized");
				}
			})
		},
		saveChangeOrderRef(value) {
			let vm = this

			this.postData("JobChangeOrder/save",{id: this.changeOrder.id,change_order_ref: this.changeOrderRef},true).then(response => {
				if (response.success)
					vm.alertSaved("Change order ref # saved")
			})
		},
		deleteChangeOrder() {
			this.deleteConfirmModal.visible = true
		},
		confirmDeleteChangeOrder() {
			let vm = this

			let payload = {
				id: this.changeOrder.id
			}

			this.postData("JobChangeOrder/delete",payload,true).then(response => {
				if (response.success) {
					vm.alertSuccess("Change order deleted")
					vm.$router.push("/project/"+this.$store.project.slug+"/change-orders")
				}
			})
		},
		compileWorkCompletedSectionFromWorksheets() {
			let vm = this

			let params = {
				filters: [
					["change_quote_id",this.changeOrder.id]
				]
			}

			this.getJSON("ChangeWorksheet/get",params).then(response => {
				if (response.success) {
					let changeOrderNumber = "CO" + vm.$store.project.job_num + "-" + vm.changeOrder.quote_num

					let compiled = vm.joinWorksheets(response.data,changeOrderNumber)

					vm.changeOrder.work_completed_description = compiled.description
				}
			})
		},
		regenerateTasksSection() {
			let vm = this

			//delete current tasks
			let promises = []
			this.changeOrderTasks.forEach(task => {
				promises.push(vm.postData("JobChangeOrderObject/delete",{id: task.id}))
			})

			Promise.all(promises).then(responses => {
				vm.fetchWorksheets().then(response => {
					let worksheets = response.data

					let joined = vm.joinWorksheets(worksheets)

					let serviceTasks = joined.serviceTasks

					vm.getJSON("ServiceContractTask/get", {filters: [["contract_id", 0]]}).then(taskResponse => {
						let taskSettings = []
						if (taskResponse.success)
							taskSettings = taskResponse.data

						serviceTasks = serviceTasks.map(task => {
							let taskId = task.task_id
							if (!taskId)
								taskId = task.id

							let settings = taskSettings.find(setting => setting.id == taskId)
							if (settings) {
								let unitPrice = +settings.unit_price
								let unitCost = +settings.unit_cost
								task.unit_price = unitPrice.toFixed(2)
								task.unit_cost = unitCost.toFixed(2)
							}

							task.quote_id = vm.changeOrder.id
							task.type = "Task"

							delete task.id

							return task
						})

						serviceTasks.forEach(task => {
							promises.push(vm.saveObject({payload: task},true,"JobChangeOrderObject"))
						})

						Promise.all(promises).then(responses => {
							vm.fetchChangeOrder()
						})
					})
				})
			})
		},
		regenerateMaterialSection() {
			let vm = this

			//delete current materials
			let promises = []
			this.changeOrderMaterials.forEach(mat => {
				promises.push(vm.postData("JobChangeOrderObject/delete",{id: mat.id}))
			})

			Promise.all(promises).then(responses => {
				vm.fetchWorksheets().then(response => {
					let worksheets = response.data

					let joined = vm.joinWorksheets(worksheets)

					let materials = vm.normalizeMaterials(joined.materials)

					materials = materials.map(mat => {
						mat.quote_id = vm.changeOrder.id

						return mat
					})

					vm.addMaterials(materials).then(response => {
						vm.fetchChangeOrder()
					})
				})
			})
		},
		regenerateEquipmentSection() {
			let vm = this

			//delete current requipment
			let promises = []
			this.changeOrderEquipment.forEach(eq => {
				promises.push(vm.postData("JobChangeOrderObject/delete",{id: eq.id}))
			})

			Promise.all(promises).then(responses => {
				vm.fetchWorksheets().then(response => {
					let worksheets = response.data

					let joined = vm.joinWorksheets(worksheets)

					let equipment = joined.equipment

					vm.getJSON("EquipmentType/get").then(typeResponse => {
						let equipmentTypes = []
						if (typeResponse.success)
							equipmentTypes = typeResponse.data

						let lines = equipment.map(eq => {
							let typeId = eq.type_id
							if (!typeId && eq.EquipmentType)
								typeId = eq.EquipmentType.id

							let equipmentType = equipmentTypes.find(setting => setting.id == typeId)
							if (!equipmentType && eq.EquipmentType)
								equipmentType = eq.EquipmentType

							let unit = "hour"
							if (equipmentType && equipmentType.charge_out_rate_unit)
								unit = equipmentType.charge_out_rate_unit
							else if (eq.qty_unit)
								unit = eq.qty_unit

							let typeName = ""
							let rate = 0
							let chargeOutRatesJson = null

							if (equipmentType) {
								typeName = equipmentType.name
								chargeOutRatesJson = equipmentType.charge_out_rates_json

								let resolved = vm.resolveEquipmentChargeOutRate(
									equipmentType.charge_out_rate,
									equipmentType.charge_out_rates_json,
									unit
								)
								rate = resolved.rate
								unit = resolved.unit
							}
							else if (eq.description)
								typeName = eq.description

							return {
								type: "Equipment",
								quote_id: vm.changeOrder.id,
								equipment_type_id: typeId,
								typeName: typeName,
								name: typeName,
								charge_out_rates_json: chargeOutRatesJson,
								qty: eq.qty,
								qty_unit: unit,
								unit: unit,
								rate: rate
							}
						})

						promises = []

						lines.forEach(line => {
							promises.push(vm.saveObject({payload: line},true,"JobChangeOrderObject"))
						})

						Promise.all(promises).then(responses => {
							vm.fetchChangeOrder()
						})
					})
				})
			})
		},
		regenerateLabourSection() {
			let vm = this

			//delete current requipment
			let promises = []
			this.changeOrderLabour.forEach(eq => {
				promises.push(vm.postData("JobChangeOrderObject/delete",{id: eq.id}))
			})

			let params = {
				filters: [
					["change_quote_id",this.changeOrder.id]
				],
				include: ["TimeLog"]
			}

			let fetchPromises = [
				this.getJSON("TimeLogDetailedClaim/get",params)
			]

			if (vm.chargeOutGroups.length == 0)
				fetchPromises.push(this.fetchChargeOutGroups())

			Promise.all(fetchPromises).then(responses => {

				let response = responses[0]
				
				if (response.success) {
					let labourLines = []

					response.data.forEach(claim => {
						let claimClassification = false, foundIndex = -1, chargeOutGroupName = ""
						if ("TimeLog" in claim && claim.TimeLog) {
							try {
								claimClassification = JSON.parse(claim.TimeLog.classification_json)
							}
							catch (e) {}
						

						let chargeOutGroup = false
						if (claimClassification && "group_id" in claimClassification && claimClassification.group_id)
							chargeOutGroup = vm.chargeOutGroups.find(group => group.id == claimClassification.group_id)

						if (!chargeOutGroup)
							chargeOutGroup = {
								label: "Unclassified",
								charge_out_rate: 0,
								ot_charge_out_rate: 0,
								ot2x_charge_out_rate: 0,
								ot25x_charge_out_rate: 0
							}

							if (chargeOutGroup) {
								if (+claim.regular_hours > 0) {

									chargeOutGroupName = chargeOutGroup.label

									foundIndex = labourLines.findIndex(line => line.chargeOutGroupName == chargeOutGroupName)

									if (foundIndex >= 0) {
										labourLines[foundIndex].hrs += +claim.regular_hours
									}
									else {
										let rate = 0
										if (chargeOutGroup) 
											rate = +chargeOutGroup.charge_out_rate
										
										labourLines.push({
											chargeOutGroupName,
											hrs: +claim.regular_hours,
											rate
										})
									}
								}
								if (+claim.overtime_hours_15x > 0) {

									chargeOutGroupName = chargeOutGroup.label + " (OT)"

									foundIndex = labourLines.findIndex(line => line.chargeOutGroupName == chargeOutGroupName)

									if (foundIndex >= 0) {
										labourLines[foundIndex].hrs += +claim.overtime_hours_15x
									}
									else {

										let rate = 0
										if (chargeOutGroup) 
											rate = +chargeOutGroup.ot_charge_out_rate
										
										labourLines.push({
											chargeOutGroupName: chargeOutGroupName,
											hrs: +claim.overtime_hours_15x,
											rate
										})
									}
								}
								if (+claim.overtime_hours_2x > 0) {

									chargeOutGroupName = chargeOutGroup.label + " (2x OT)"

									foundIndex = labourLines.findIndex(line => line.chargeOutGroupName == chargeOutGroupName)

									if (foundIndex >= 0) {
										labourLines[foundIndex].hrs += +claim.overtime_hours_2x
									}
									else {
										let rate = 0
										if (chargeOutGroup) 
											rate = +chargeOutGroup.ot2x_charge_out_rate
										
										labourLines.push({
											chargeOutGroupName: chargeOutGroupName,
											hrs: +claim.overtime_hours_2x,
											rate
										})
									}
								}
								if (+claim.overtime_hours_25x > 0) {

									chargeOutGroupName = chargeOutGroup.label + "(2.5x OT)"

									foundIndex = labourLines.findIndex(line => line.chargeOutGroupName == chargeOutGroupName)

									if (foundIndex >= 0) {
										labourLines[foundIndex].hrs += +claim.overtime_hours_25x
									}
									else {
										let rate = 0
										if (chargeOutGroup) 
											rate = +chargeOutGroup.ot25x_charge_out_rate
										
										labourLines.push({
											chargeOutGroupName: chargeOutGroupName,
											hrs: +claim.overtime_hours_25x,
											rate
										})
									}
								}
							}
						}
					})

					labourLines.forEach(line => {

						line.type = "Labour"
						line.quote_id = vm.changeOrder.id

						promises.push(vm.saveObject({payload: line},true,"JobChangeOrderObject"))
					})

					Promise.all(promises).then(responses => {
						vm.fetchChangeOrder()
					})
				}
			})
			
		},
		quoteStatusChanged() {
			let vm = this

			this.$nextTick(() => {
				if (vm.changeOrderStatusCompleted && !vm.changeOrder.completed)
					vm.changeOrder.completed = vm.moment().format("YYYY-MM-DD")
			})
		},
		saveChangeOrder(finalize) {
			let vm = this

			let fieldsToUse = [
				"id",
				"is_cost_plus",
				"description",
				"issue_date",
				"payment_terms",
				"attn",
				"customer_notes",
				"bottom_text",
				"request_num",
				"applied_sales_tax_json",
				"expires_in_days",
				"quote_status",
				"hide_from_detailed_reporting",
				"work_completed_description",
				"completed"
			]

			let payload = {}

			fieldsToUse.forEach(field => {
				if (field in vm.changeOrder)
					payload[field] = vm.changeOrder[field]
			})

			if (payload.id == 'new')
				delete payload.id

			payload.job_id = this.$store.project.id

			if (finalize)
				payload.is_finalized = 1

			if (!this.changeOrderStatusCompleted)
				payload.completed = null
			
			this.working = true

			return new Promise((resolve,reject) => {

				vm.postData("JobChangeOrder/save",payload,true).then(response => {
					vm.working = false
					if (response.success) {
						vm.alertSaved("Change order saved")

						vm.previewRefresher++

						if (finalize) {
							vm.changeOrder.is_finalized = 1
							if (vm.activeSection == 'Finalizing')
								vm.activeSection = "Delivery"

							vm.fetchChangeOrder()
						}

						if (!("id" in payload)) {
							window.history.pushState({},"","/project/"+vm.$store.project.slug+"/change-orders/"+response.data.slug)
							vm.changeOrder.id = response.data.record_id
							vm.$emit('slugChange',response.data.slug)
						}

						resolve(response)
					}
				})
			})
		},
		fetchCustomer() {
			let vm = this

			let params = {
				include: ["Contact"]
			}

			this.getJSON("Contact/find/"+vm.$store.project.customer_id,params).then(response => {
				if (response.success) {
					vm.customer = response.data

					try {

						// Customer has settings? Use them. No settings? Use company settings
						if (Object.keys(vm.clientQuoteSettings).length == 0) {
							if (vm.customer.settings_json) {
								vm.setQuoteDisplaySettings(vm.customer.settings_json)
							} else if (vm.$store.company && 'CompanySetting' in vm.$store.company) {
								vm.setQuoteDisplaySettings(vm.$store.company.CompanySetting.job_defaults_json)
							}
						}
					} catch (ex) {}

					vm.clientRateOverrides = []
					try {
						vm.clientRateOverrides = JSON.parse(customer.charge_out_group_overrides_json)
					}
					catch (ex) {}
				}
			})
		},
		fetchWorksheets() {
			let vm = this

			let params = {
				filters: [["change_quote_id",this.changeOrder.id]]
			}

			return new Promise((resolve,reject) => {
				vm.getJSON("ChangeWorksheet/get",params).then(response => {
					if (response.success) 
						vm.worksheets = response.data

					resolve(response)
				})
			})
		},
		fetchChangeOrder() {
			let vm = this

			let params = {
				include: ["JobChangeOrderObject",{model: "JobInvoiceTask",fields:["id"]}]
			}

			this.getJSON("JobChangeOrder/find/"+this.changeOrderSlug,params).then(response => {
				if (response.success) {
					vm.changeOrder = response.data
					vm.previewRefresher++

					vm.changeOrderRef = response.data.change_order_ref

					// Settings for change order exist? Use them
					if (response.data.settings_json)
						vm.setQuoteDisplaySettings(response.data.settings_json)

					vm.setChangeOrderObjects(response.data.JobChangeOrderObject)

					delete vm.changeOrder.JobChangeOrderObject
				}
			})
		},
		fetchChargeOutGroups() {
			let vm = this

			return new Promise((resolve,reject) => {
				vm.getJSON("ClassificationGroup/get").then(response => {
					if (response.success)
						vm.chargeOutGroups = response.data

					resolve(response)
				})
			})
		}
	},
	created() {
		if (this.changeOrderSlug)
			this.fetchChangeOrder()

		if ('customer_id' in this.$store.project)
			this.fetchCustomer()

		this.setPermFlags()

		this.fetchChargeOutGroups()
		this.fetchJobPersonnelRoles()
		this.fetchChangeOrderStatuses()
	}
}
</script>